import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { getPlanForDates } from "../api/plans";
import { getMeals } from "../api/meals";
import ForageLogo from "../components/ForageLogo";

function greeting(){
  const h = new Date().getHours();
  if(h < 12) return "Good morning";
  if(h < 17) return "Good afternoon";
  return "Good evening";
}

function toDateStr(date){
  const y = date.getFullYear();
  const m = String(date.getMonth()+1).padStart(2,"0");
  const d = String(date.getDate()).padStart(2,"0");
  return `${y}-${m}-${d}`;
}

const MEAL_TYPES  = ["breakfast","lunch","dinner","snacks"];
const MEAL_LABELS = { breakfast:"Breakfast", lunch:"Lunch", dinner:"Dinner", snacks:"Snacks" };

const FEATURES = [
  {
    to: "/meals",
    title: "Meal Library",
    desc: "Browse, add, and edit your household's saved recipes.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18M3 12h18M3 18h12"/>
      </svg>
    ),
  },
  {
    to: "/planner",
    title: "Meal Planner",
    desc: "Schedule breakfast, lunch, dinner, and snacks for the week.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8"  y1="2" x2="8"  y2="6"/>
        <line x1="3"  y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
  {
    to: "/cook",
    title: "Cook with My Ingredients",
    desc: "Enter what you have on hand and discover meals you can make.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
        <path d="M16 3H8v4h8V3z"/>
      </svg>
    ),
  },
  {
    to: "/grocery",
    title: "Grocery List",
    desc: "Auto-generate a shopping list from your planned meals.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 0 1-8 0"/>
      </svg>
    ),
  },
];

export default function DashboardPage({ meals, setMeals }){
  const { user, householdId } = useContext(AuthContext);
  const navigate = useNavigate();

  const firstName  = user?.displayName?.split(" ")[0] || "there";
  const todayStr   = toDateStr(new Date());
  const todayLabel = new Date().toLocaleDateString("en-US",{
    weekday:"long", month:"long", day:"numeric"
  });

  // null = loading, object = loaded
  const [todayPlan, setTodayPlan] = useState(null);

  useEffect(()=>{
    if(!householdId) return;
    async function load(){
      const [planData, mealData] = await Promise.all([
        getPlanForDates(householdId, [todayStr]),
        meals.length > 0 ? Promise.resolve(meals) : getMeals(householdId)
      ]);
      setTodayPlan(planData[todayStr] || {});
      if(mealData !== meals) setMeals(mealData);
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[householdId]);

  const mealMap = Object.fromEntries((meals||[]).map(m=>[m.id, m]));

  return(
    <div className="dashboardPage">

      {/* HERO */}
      <div className="dashboardHero">
        <div className="dashboardHeroLogo">
          <ForageLogo size={52} color="var(--olive)" />
        </div>
        <div>
          <h1 className="dashboardGreeting">{greeting()}, {firstName}.</h1>
          <p className="dashboardSubtitle">What would you like to do today?</p>
        </div>
      </div>

      {/* TODAY'S PLAN */}
      <div className="dashboardTodayStrip">
        <div className="dashboardTodayHeader">
          <div className="dashboardTodayTitleGroup">
            <span className="dashboardTodayTitle">Today</span>
            <span className="dashboardTodayDate">{todayLabel}</span>
          </div>
          <button
            className="dashboardTodayLink"
            onClick={()=>navigate("/planner")}
          >
            Open planner →
          </button>
        </div>

        {todayPlan === null ? (
          <div className="dashboardTodayGrid">
            {MEAL_TYPES.map(t=>(
              <div key={t} className="dashboardTodayCell">
                <span className="dashboardTodayCellType">{MEAL_LABELS[t]}</span>
                <div className="skeletonCard" style={{height:20, marginTop:8}} />
              </div>
            ))}
          </div>
        ) : (
          <div className="dashboardTodayGrid">
            {MEAL_TYPES.map(t=>{
              const ids = todayPlan[t] || [];
              return(
                <div key={t} className="dashboardTodayCell">
                  <span className={`dashboardTodayCellType calMealType-${t}`}>
                    {MEAL_LABELS[t]}
                  </span>
                  {ids.length === 0 ? (
                    <span className="dashboardTodayEmpty">Nothing planned</span>
                  ) : (
                    ids.map(id=>{
                      const meal = mealMap[id];
                      return meal
                        ? <span key={id} className="dashboardTodayMeal">{meal.name}</span>
                        : null;
                    })
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* FEATURE CARDS */}
      <div className="dashboardGrid">
        {FEATURES.map(f=>(
          <button
            key={f.to}
            className="dashboardCard"
            onClick={()=>navigate(f.to)}
          >
            <div className="dashboardCardIcon">{f.icon}</div>
            <h3 className="dashboardCardTitle">{f.title}</h3>
            <p className="dashboardCardDesc">{f.desc}</p>
          </button>
        ))}
      </div>

    </div>
  );
}
